import React, {useImperativeHandle, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {useComponentProps} from 'helpers/hooks/utils';
import constants from 'helpers/constants';
import DialogContent from 'components/atoms/Dialogs/DialogContent/DialogContent';
import InlineForm from 'components/organisms/Forms/InlineForm/InlineForm';
import DialogFooter from 'components/molecules/Dialogs/DialogFooter/DialogFooter';
import Button from 'components/atoms/Buttons/Button/Button';
import DialogHeader from 'components/molecules/Dialogs/DialogHeader/DialogHeader';
import {Span} from 'components/atoms/Text/Typography/Typography';
import StyledFieldAddDialog from 'components/organisms/Dialogs/FieldAddDialog/FieldAddDialog.styles';
import {useClientCustomFieldGroups} from 'services/client/customField/customField.utils';
import utils from 'helpers/utils';
import Icon from 'components/atoms/Icons/Icon/Icon';
import Add from '@mui/icons-material/Add';
import Close from '@mui/icons-material/Close';

const FieldAddDialog = React.forwardRef((props, ref) => {
  const {
    onSubmit,
    ...innerProps
  } = useComponentProps(props, 'FieldAddDialog', {
    children: ['header', 'content', 'footer']
  });

  const innerRef = useRef(null);
  const formRef = useRef(null);

  const [error, setError] = useState(null);
  const [dirty, setDirty] = useState(null);
  const [validation, setValidation] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const groups = useClientCustomFieldGroups();

  useImperativeHandle(ref, () => innerRef.current);

  const fields = useMemo(() => {
    return [
      {
        name: 'label',
        label: 'Label',
        inlineLabel: 'label',
        type: constants.formFieldTypes.text,
        validation: constants.formFieldValidationTypes.text,
        initial: '',
        required: true,
        FormFieldProps: {
          variant: 'staticLabel'
        },
      },
      {
        name: 'groupName',
        label: 'Group',
        inlineLabel: 'group',
        type: constants.formFieldTypes.autocomplete,
        validation: constants.formFieldValidationTypes.text,
        conversion: constants.formFieldConversionTypes.label,
        initial: '',
        required: true,
        options: groups,
        FormFieldProps: {
          variant: 'staticLabel',
          createOption: true
        }
      },
      {
        name: 'renderer',
        label: 'Type',
        inlineLabel: 'type',
        type: constants.formFieldTypes.autocomplete,
        validation: constants.formFieldValidationTypes.text,
        conversion: constants.formFieldConversionTypes.value,
        initial: '',
        required: true,
        options: 'customFieldRendererTypes',
        FormFieldProps: {
          variant: 'staticLabel'
        }
      },
      {
        name: 'tooltip',
        label: 'Description',
        inlineLabel: 'description',
        placeholder: 'Describe the field',
        type: constants.formFieldTypes.richText,
        validation: constants.formFieldValidationTypes.text,
        initial: '',
        FormFieldProps: {
         variant: 'staticLabel'
        }
      },
      {
        name: 'autoLookup',
        label: 'Auto lookup',
        inlineLabel: 'auto lookup',
        type: constants.formFieldTypes.list,
        validation: constants.formFieldValidationTypes.boolean,
        conversion: constants.formFieldConversionTypes.value,
        options: 'toggleFilesOnline',
        initial: false,
        required: true,
        FormFieldProps: {
          variant: 'staticLabel',
          ListProps: {
            catchFocus: false,
            orientation: 'horizontal',
            gap: 16
          }
        }
      },
      {
        name: 'autoLookupDescription',
        label: 'Lookup prompt',
        inlineLabel: 'lookup prompt',
        placeholder: 'Describe the field value',
        type: constants.formFieldTypes.textarea,
        validation: constants.formFieldValidationTypes.text,
        initial: '',
        FormFieldProps: {
          variant: 'staticLabel'
        }
      },
    ]
  }, [groups]);

  const handleSubmit = (values, actions) => {
    setSubmitting(true);

    utils.asPromise(onSubmit)(values)
      .then((close) => {
        if (close) {
          innerProps.onClose?.(null, 'saveButtonClick');
        }
      })
      .catch(() => {
        setError('Creating field failed');
      })
      .finally(() => {
        actions.setSubmitting(false);
        setSubmitting(false);
      });
  };

  const handleValidating = (isValidating, isDirty, hasErrors) => {
    setDirty(isDirty);
    if (hasErrors) {
      setValidation('Please check if all fields have the correct values');
    } else {
      setValidation(null);
    }
  }

  const handleSubmitClick = () => {
    formRef.current?.submit();
  };

  const handleCancel = (e) => {
    innerRef.current?.close?.(e, 'cancelButtonClick');
  }

  const handleClose = (e, reason) => {
    if ((!submitting && !error && !dirty) || ['escapeKeyDown', 'closeButtonClick', 'cancelButtonClick'].includes(reason)) {
      innerProps.onClose?.(e, reason);
    }
  }

  const renderButtons = () => {
    return <React.Fragment>
      <Button children={'Cancel'}
              variant="text"
              startIcon={<Icon icon={Close}/>}
              onClick={handleCancel}/>
      <Button disabled={submitting}
              type="submit"
              color="success"
              variant="contained"
              children={'Create field'}
              startIcon={<Icon icon={Add}/>}
              onClick={handleSubmitClick}/>
    </React.Fragment>
  }

  return <StyledFieldAddDialog ref={innerRef} {...innerProps} onClose={handleClose}>
    <DialogHeader className="FieldAddDialog-header"
                  title="Add field" />
    <DialogContent className="FieldAddDialog-content" >
      <InlineForm ref={formRef} fields={fields}
                  onValidating={handleValidating}
                  onSubmit={handleSubmit} />
    </DialogContent>
    <DialogFooter className="FieldAddDialog-footer"
                  info={(error ?? validation) ? <Span color="error">{error ?? validation}</Span> : null}
                  buttons={renderButtons()} />
  </StyledFieldAddDialog>
});

FieldAddDialog.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]),
  onSubmit: PropTypes.func
};

export default FieldAddDialog;
